// HomePage.tsx — v2
import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  Activity, Brain, Zap, Shield, Database,
  ArrowRight, TrendingUp, TrendingDown, Check,
  BarChart2, ChevronRight, Cpu,
} from 'lucide-react'
import SpinningLogo from '../components/SpinningLogo'
import { trackHomePageVisit } from '../utils/visitorTracker'

let PRODUCT_URL: string
if (!import.meta.env.VITE_NODE_ENV || import.meta.env.VITE_NODE_ENV === 'dev') {
  PRODUCT_URL = 'http://localhost:5173/stocksignalspro#home'
} else {
  PRODUCT_URL = import.meta.env.VITE_PRODUCT_URL || 'https://stocksignalspro.larxnet.com/'
}

interface Props { dark: boolean; apiBase: string; productUrl: string }

/* ─── Ticker ──────────────────────────────────────────────────────────────── */
/* const TICKER_SYMBOLS = [
  { s: 'AAPL', v: 2.34 }, { s: 'NVDA', v: 5.12 }, { s: 'MSFT', v: 1.89 },
  { s: 'TSLA', v: -3.45 }, { s: 'AMZN', v: 0.67 }, { s: 'GOOGL', v: -1.23 },
  { s: 'META', v: 2.78 }, { s: 'AMD', v: 4.33 }, { s: 'NFLX', v: -2.10 },
  { s: 'CRM', v: 1.67 }, { s: 'ORCL', v: 1.20 }, { s: 'INTC', v: -0.45 },
]

const Ticker: React.FC = () => {
  const items = [...TICKER_SYMBOLS, ...TICKER_SYMBOLS, ...TICKER_SYMBOLS]
  return (
    <div className="overflow-hidden py-2.5">
      <div style={{ animation: 'ticker 42s linear infinite', width: 'max-content', display: 'flex', gap: '12px' }}>
        {items.map((t, i) => (
          <div
            key={i}
            className="flex-shrink-0 flex items-center gap-2.5 px-4 py-1.5 rounded-lg"
            style={{
              background: 'rgba(26,28,40,0.85)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderTopColor: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <span
              className="text-xs font-semibold tracking-widest"
              style={{ fontFamily: 'var(--font-mono)', color: 'var(--c-text)' }}
            >
              {t.s}
            </span>
            <span
              className="flex items-center gap-1 text-xs font-medium"
              style={{ fontFamily: 'var(--font-mono)', color: t.v > 0 ? 'var(--c-green)' : 'var(--c-red)' }}
            >
              {t.v > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {t.v > 0 ? '+' : ''}{t.v.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
 */
/* ─── Stat (scroll-triggered) ─────────────────────────────────────────────── */
interface StatProps { value: string; label: string; delay: number }

const Stat: React.FC<StatProps> = ({ value, label, delay }) => {
  const [visible, setVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true) },
      { threshold: 0.2 }
    )
    if (ref.current) obs.observe(ref.current)
    return () => obs.disconnect()
  }, [])
  return (
    <div
      ref={ref}
      className="text-center"
      style={{ animation: visible ? `fadeUp 0.6s ease-out ${delay}s both` : 'none' }}
    >
      <div
        className="font-display mb-1"
        style={{
          fontSize: 'clamp(2.2rem, 4vw, 3rem)',
          background: 'linear-gradient(120deg, var(--c-cyan) 0%, var(--c-indigo) 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}
      >
        {value}
      </div>
      <div className="text-sm font-medium" style={{ color: 'var(--c-text-3)' }}>{label}</div>
    </div>
  )
}

/* ─── Feature card ─────────────────────────────────────────────────────────── */
interface FeatureItem {
  icon: React.ReactNode
  title: string
  desc: string
  glowBg: string   // rgba string for the hover glow div
  iconColor: string // hex string for the icon
}

const FeatureCard: React.FC<FeatureItem & { delay: number }> = ({
  icon, title, desc, glowBg, iconColor, delay,
}) => (
  <div
    className="group glass relative overflow-hidden rounded-2xl p-6 transition-all duration-500 hover:-translate-y-1.5 cursor-default"
    style={{ animation: `fadeUp 0.55s ease-out ${delay}s both` }}
  >
    {/* Hover glow blob */}
    <div
      className="absolute -bottom-8 -right-8 w-32 h-32 rounded-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
      style={{ background: glowBg, filter: 'blur(32px)' }}
    />
    {/* Icon box */}
    <div
      className="relative w-11 h-11 rounded-xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110"
      style={{
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.10)',
        color: iconColor,
      }}
    >
      <div className="w-5 h-5">{icon}</div>
    </div>
    <h3 className="font-semibold text-base mb-2" style={{ color: 'var(--c-text)' }}>{title}</h3>
    <p className="text-sm leading-relaxed" style={{ color: 'var(--c-text-2)' }}>{desc}</p>
  </div>
)

/* ─── How-it-works step ────────────────────────────────────────────────────── */
interface StepItem { n: string; title: string; desc: string }

interface PricingPlan {
  name: string;
  price: string;
  period: string;
  desc: string;
  planFeatures: string[];
  cta: string;
  href: string;
  featured: boolean;
}

const Step: React.FC<StepItem & { highlight: boolean; delay: number }> = ({
  n, title, desc, highlight, delay,
}) => (
  <div
    className={`flex gap-5 p-5 rounded-2xl transition-all duration-300 ${highlight ? 'glass' : ''}`}
    style={{ animation: `fadeUp 0.5s ease-out ${delay}s both` }}
  >
    <div
      className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center font-mono text-sm font-medium"
      style={{
        background: highlight ? 'rgba(34,211,238,0.10)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${highlight ? 'rgba(34,211,238,0.28)' : 'rgba(255,255,255,0.07)'}`,
        color: highlight ? 'var(--c-cyan)' : 'var(--c-text-3)',
        fontFamily: 'var(--font-mono)',
      }}
    >
      {n}
    </div>
    <div>
      <div className="font-semibold mb-1.5" style={{ color: highlight ? 'var(--c-text)' : 'var(--c-text-2)' }}>
        {title}
      </div>
      <div className="text-sm leading-relaxed" style={{ color: 'var(--c-text-3)' }}>{desc}</div>
    </div>
  </div>
)


/* ─── Dashboard mockup ─────────────────────────────────────────────────────── */
const DashboardMockup: React.FC = () => (
  <div style={{ animation: 'floatCard 7s ease-in-out infinite', maxWidth: 460, width: '100%' }}>
    <div
      className="glass rounded-2xl overflow-hidden"
      style={{ boxShadow: '0 0 60px rgba(34,211,238,0.08), 0 40px 80px rgba(0,0,0,0.5)' }}
    >
      {/* Title bar */}
      <div
        className="flex items-center justify-between px-5 py-3"
        style={{ borderBottom: '1px solid var(--c-border)' }}
      >
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--c-red)' }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--c-amber)' }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--c-green)' }} />
        </div>
        <span className="text-xs" style={{ fontFamily: 'var(--font-mono)', color: 'var(--c-text-3)' }}>
          StockSignals Pro
        </span>
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--c-green)', animation: 'driftOrb 2s ease-in-out infinite' }} />
          <span className="text-xs font-medium" style={{ fontFamily: 'var(--font-mono)', color: 'var(--c-green)' }}>LIVE</span>
        </div>
      </div>

      {/* Chart */}
      <div className="relative px-5 pt-5 pb-3" style={{ height: 110 }}>
        <svg viewBox="0 0 400 75" className="w-full h-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M0,58 L35,48 L75,53 L115,33 L155,38 L195,22 L235,27 L275,12 L315,18 L355,8 L400,3"
            fill="none" stroke="#22d3ee" strokeWidth="1.8"
          />
          <path
            d="M0,58 L35,48 L75,53 L115,33 L155,38 L195,22 L235,27 L275,12 L315,18 L355,8 L400,3 L400,75 L0,75 Z"
            fill="url(#chartFill)"
          />
        </svg>
        {/* Scan line */}
        <div
          className="absolute left-0 right-0 pointer-events-none"
          style={{
            height: '1.5px',
            background: 'linear-gradient(90deg, transparent, rgba(34,211,238,0.5), transparent)',
            animation: 'scanLine 5s ease-in-out infinite',
          }}
        />
         {/* Drop light below chart */}
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none"
          style={{
            width: '70%',
            height: '28px',
            background: 'radial-gradient(ellipse at 50% 100%, rgba(34,211,238,0.22) 0%, transparent 75%)',
            filter: 'blur(6px)',
          }}
        />
      </div>

      {/* Indicators */}
      <div className="grid grid-cols-3" style={{ borderTop: '1px solid var(--c-border)' }}>
        {[
          { label: 'RSI', value: '67.4', color: 'var(--c-amber)' },
          { label: 'MACD', value: '▲ BUY', color: 'var(--c-green)' },
          { label: 'EMA', value: '+0.89σ', color: 'var(--c-cyan)' },
        ].map((ind, i) => (
          <div
            key={ind.label}
            className="p-3 text-center"
            style={{ borderRight: i < 2 ? '1px solid var(--c-border)' : 'none' }}
          >
            <div className="text-xs mb-1" style={{ fontFamily: 'var(--font-mono)', color: 'var(--c-text-3)' }}>
              {ind.label}
            </div>
            <div className="text-sm font-medium" style={{ fontFamily: 'var(--font-mono)', color: ind.color }}>
              {ind.value}
            </div>
          </div>
        ))}
      </div>

      {/* AI signal */}
      <div
        className="m-4 p-3.5 rounded-xl"
        style={{
          background: 'rgba(34,211,238,0.05)',
          border: '1px solid rgba(34,211,238,0.15)',
        }}
      >
        <div className="flex items-center gap-2 mb-1.5">
          <Brain className="w-3.5 h-3.5" style={{ color: 'var(--c-cyan)' }} />
          <span className="text-xs font-semibold" style={{ fontFamily: 'var(--font-mono)', color: 'var(--c-cyan)' }}>
            AI SIGNAL
          </span>
          <span className="ml-auto text-xs" style={{ fontFamily: 'var(--font-mono)', color: 'var(--c-text-3)' }}>NVDA</span>
        </div>
        <p className="text-xs leading-relaxed" style={{ color: 'var(--c-text-2)' }}>
          Bullish momentum. Volume confirms breakout above 200-day EMA.{' '}
          <span style={{ color: 'var(--c-green)' }}>Confidence: 87%</span>
        </p>
      </div>
    </div>
  </div>
)

/* ─── HomePage ─────────────────────────────────────────────────────────────── */
const HomePage: React.FC<Props> = ({ dark }) => {
  const [mx, setMx] = useState(50)
  const [my, setMy] = useState(50)
  const heroRef = useRef<HTMLDivElement>(null)

  // Home-page visitor tracking (Admin → Visitors). Fires at most once per tab
  // session; the backend derives IP/country/datacenter/bot from the request.
  useEffect(() => {
    trackHomePageVisit()
  }, [])

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!heroRef.current) return
      const r = heroRef.current.getBoundingClientRect()
      setMx(((e.clientX - r.left) / r.width) * 100)
      setMy(((e.clientY - r.top) / r.height) * 100)
    }
    window.addEventListener('mousemove', onMove, { passive: true })
    return () => window.removeEventListener('mousemove', onMove)
  }, [])

  // ── Data ──────────────────────────────────────────────────────────────────

  const features: FeatureItem[] = [
    {
      icon: <Activity />,
      title: 'Real-Time Signals',
      desc: 'Sub-50ms market data with live technical signals for every asset class.',
      glowBg: 'rgba(34,211,238,0.20)',
      iconColor: '#22d3ee',
    },
    {
      icon: <Brain />,
      title: 'AI-Powered Insights',
      desc: 'Surfacing opportunities before they pass.',
      glowBg: 'rgba(129,140,248,0.20)',
      iconColor: '#818cf8',
    },
    {
      icon: <BarChart2 />,
      title: 'Advanced Analytics',
      desc: '20+ indicators: EMA, VWAP, RSI, MACD, Bollinger Bands and more.',
      glowBg: 'rgba(16,185,129,0.20)',
      iconColor: '#10b981',
    },
    {
      icon: <Shield />,
      title: 'Enterprise Security',
      desc: 'Bank-grade 256-bit AES encryption, SOC 2 compliant, zero-trust architecture.',
      glowBg: 'rgba(34,211,238,0.15)',
      iconColor: '#22d3ee',
    },
    {
      icon: <Zap />,
      title: 'Agentic Automation',
      desc: 'Autonomous agents that monitor, analyse, and alert without human intervention.',
      glowBg: 'rgba(245,158,11,0.20)',
      iconColor: '#f59e0b',
    },
    {
      icon: <Database />,
      title: 'Data Management',
      desc: 'Ingest, normalise and query terabytes of market data with near-zero latency.',
      glowBg: 'rgba(244,63,94,0.18)',
      iconColor: '#fb7185',
    },
  ]

  const steps: StepItem[] = [
    { n: '01', title: 'Connect', desc: 'Plug in your data sources in minutes. API-first with 100+ native integrations.' },
    { n: '02', title: 'Analyse', desc: 'AI agents process your data in real-time and surface actionable patterns.' },
    { n: '03', title: 'Decide', desc: 'Get ranked, contextualised signals delivered to your dashboard or via webhook.' },
    { n: '04', title: 'Act', desc: 'Execute with confidence — backed by transparent reasoning and audit trails.' },
  ]

  const plans: PricingPlan[] = [
    {
      name: 'Starter',
      price: '$Free',
      period: '',
      desc: 'Perfect to explore the platform',
      planFeatures: ['Up to 10 signals/day', 'Standard indicators', 'Community support', '5 alerts'],
      cta: 'Get Started Free',
      href: '/register',
      featured: false,
    },
    {
      name: 'Professional',
      price: '$49',
      period: '/month',
      desc: 'For serious traders & analysts',
      planFeatures: ['Unlimited signals', 'All 20+ indicators', 'AI insights', 'Priority support', 'Unlimited alerts', 'API access'],
      cta: 'Start Free Trial',
      href: '/register',
      featured: true,
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      desc: 'For teams & institutions',
      planFeatures: ['Everything in Pro', 'Dedicated infrastructure', 'Custom integrations', 'SLA guarantee', 'White-label option'],
      cta: 'Talk to Sales',
      href: '/contact',
      featured: false,
    },
  ]

  const sectionBg = 'var(--c-bg)'
  const altBg = dark ? 'rgba(0,0,0,0.20)' : 'rgba(0,0,0,0.04)'
  const borderColor = 'var(--c-border)'

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Helmet>
        <title>Larxnet | Transform Data Into Decisions</title>
        <meta
          name='description'
          content='Enterprise-grade data management and analytics platform powered by AI. Real-time signals, autonomous agents, and advanced analytics at scale.'
        />
      </Helmet>

      <div className='min-h-screen page-enter' style={{ background: sectionBg }}>
        {/* Mouse-follow spotlight overlay */}
        <div
          className='fixed inset-0 pointer-events-none'
          style={{
            background: `radial-gradient(600px circle at ${mx}% ${my}%, rgba(34,211,238,0.045) 0%, rgba(99,102,241,0.025) 40%, transparent 70%)`,
            zIndex: 0,
            transition: "background 0.08s ease",
          }}
        />

        {/* ── HERO ──────────────────────────────────────────────────────── */}
        <section
          ref={heroRef}
          className='relative overflow-hidden'
          style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
        >
          {/* Dot grid */}
          <div className='absolute inset-0 bg-dots pointer-events-none' />

          {/* Aurora orbs — parallax */}
          <div
            className='absolute pointer-events-none'
            style={{
              width: 680,
              height: 480,
              top: "-8%",
              left: "-5%",
              borderRadius: "50%",
              background:
                "radial-gradient(ellipse, rgba(8,145,178,0.55) 0%, rgba(14,116,144,0.2) 50%, transparent 70%)",
              filter: "blur(60px)",
              opacity: 0.22,
              animation: "driftOrb 22s ease-in-out infinite",
              transform: `translate(${(mx - 50) * 0.07}%, ${(my - 50) * 0.07}%)`,
              transition: "transform 1.4s cubic-bezier(0.23,1,0.32,1)",
            }}
          />
          <div
            className='absolute pointer-events-none'
            style={{
              width: 560,
              height: 560,
              bottom: "-15%",
              right: "-8%",
              borderRadius: "50%",
              background: "radial-gradient(ellipse, rgba(99,102,241,0.5) 0%, transparent 70%)",
              filter: "blur(70px)",
              opacity: 0.16,
              animation: "driftOrb 28s ease-in-out infinite",
              animationDelay: "-10s",
              transform: `translate(${(mx - 50) * -0.04}%, ${(my - 50) * -0.04}%)`,
              transition: "transform 1.8s cubic-bezier(0.23,1,0.32,1)",
            }}
          />

          {/* Ticker strip */}
          {/*           <div style={{ borderBottom: `1px solid ${borderColor}` }}>
            <Ticker />
          </div>
 */}
          {/* Hero content */}
          <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
            <div className='max-w-7xl mx-auto px-6 py-20 w-full'>
              <div className='grid lg:grid-cols-2 gap-16 items-center'>
                {/* Left */}
                <div>
                  {/* Badge */}

                  <div
                    className='inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase mb-6'
                    style={{
                      fontFamily: "var(--font-mono)",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid var(--c-border)",
                      color: "var(--c-text-3)",
                    }}
                  >
                    Enterprise Data & Analytics Platform
                  </div>

                  {/* Heading */}
                  <h1
                    className='s2 font-display leading-none mb-6'
                    style={{
                      /*   fontSize: "clamp(4rem, 9vw, 8rem)",  */
                      fontSize: "clamp(3.5rem, 7vw, 6rem)",
                      color: "var(--c-text)",
                      lineHeight: 0.95,
                    }}
                  >
                    TRANSFORM
                    <br />
                    DATA INTO
                    <br />
                    <span
                      style={{
                        background:
                          "linear-gradient(120deg, var(--c-cyan) 0%, var(--c-indigo) 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      DECISIONS
                    </span>
                  </h1>

                  {/* Sub */}
                  <p
                    className='s3 text-lg leading-relaxed mb-10 max-w-lg'
                    style={{ color: "var(--c-text-1)" }}
                  >
                    A powerful enterprise analytics platform that transforms raw data into
                    intelligent, AI-driven insights and signals — engineered for scale and
                    performance. From real-time streaming data ingestion to advanced analytics and
                    smart alerts, it reveals patterns, anticipates market moves, calculates market
                    sentiment to deliver precise, executable intraday setups. Designed for investors
                    at every level, with integrated manual paper trading and AI-powered trading robot for risk-free strategy testing.
                  </p>

                  {/* CTAs */}
                  <div className='s4 flex flex-wrap items-center gap-4'>
                    <Link
                      to='/register'
                      className='btn-cta group inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-sm'
                    >
                      Get Started Free
                      <ArrowRight className='w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5' />
                    </Link>
                    <a
                      href={PRODUCT_URL}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='btn-ghost inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-sm'
                    >
                      <Activity className='w-4 h-4' style={{ color: "var(--c-cyan)" }} />
                      StockSignalsPro
                    </a>
                  </div>

                  {/* Trust strip */}
                  <div className='s5 flex flex-wrap items-center gap-5 mt-8'>
                    {["No credit card required", "SOC 2 Compliant", "99.9% uptime SLA"].map(
                      (txt) => (
                        <span
                          key={txt}
                          className='flex items-center gap-1.5 text-xs'
                          style={{ color: "var(--c-text-2)" }}
                        >
                          <Check className='w-3 h-3' style={{ color: "var(--c-cyan)" }} />
                          {txt}
                        </span>
                      ),
                    )}
                  </div>
                </div>

                {/* Right — dashboard mockup */}
                <div className='s3 flex justify-center'>
                  <DashboardMockup />
                </div>
              </div>
            </div>
          </div>

          {/* Bottom fade */}
          <div
            className='absolute bottom-0 left-0 right-0 h-28 pointer-events-none'
            style={{ background: `linear-gradient(to bottom, transparent, ${sectionBg})` }}
          />
        </section>

        {/* ── FEATURES ──────────────────────────────────────────────────── */}
        <section className='py-24 px-6 relative overflow-hidden'>
          <div className='max-w-7xl mx-auto'>
            <div className='text-center mb-16'>
              <div
                className='inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase mb-6'
                style={{
                  fontFamily: "var(--font-mono)",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid var(--c-border)",
                  color: "var(--c-text-3)",
                }}
              >
                Platform Capabilities
              </div>
              <h2
                className='font-display mb-4 leading-none'
                style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)", color: "var(--c-text)" }}
              >
                EVERYTHING TO{" "}
                <span
                  style={{
                    background: "linear-gradient(120deg, var(--c-cyan) 0%, var(--c-indigo) 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  STAY AHEAD
                </span>
              </h2>
              <p className='text-lg max-w-xl mx-auto' style={{ color: "var(--c-text-2)" }}>
                A complete analytics stack — from raw data ingestion to AI-generated signals.
              </p>
            </div>

            <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-4'>
              {features.map((f, i) => (
                <FeatureCard key={f.title} {...f} delay={i * 0.08} />
              ))}
            </div>
          </div>
        </section>

        {/* ── PRODUCTS ──────────────────────────────────────────────────── */}
        <section className='py-24 px-6'>
          <div className='max-w-6xl mx-auto'>
            <div className='text-center mb-14'>
              <div
                className='inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-widest uppercase mb-6'
                style={{
                  fontFamily: "var(--font-mono)",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid var(--c-border)",
                  color: "var(--c-text-3)",
                  boxShadow: "2px 2px 8px rgba(0,0,0,0.3)",
                }}
              >
                Our Products
              </div>
              {/*               <h2
                className='font-display leading-none'
                style={{ fontSize: "clamp(2.5rem, 6vw, 4.5rem)", color: "var(--c-text)" }}
              >
                BUILT FOR EVERY SCALE
              </h2> */}
              <h2
                className='font-display mb-4'
                style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", color: "var(--c-text)" }}
              >
                THE LARXNET{" "}
                <span
                  style={{
                    background: "linear-gradient(120deg, var(--c-cyan) 0%, var(--c-indigo) 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  SUITE
                </span>
              </h2>
              <p className='max-w-lg mx-auto' style={{ color: "var(--c-text-2)" }}>
                A growing ecosystem of intelligent data products for modern market participants.
              </p>
            </div>

            <div className='grid lg:grid-cols-2 gap-6'>
              {/* Product 1 — StockSignals Pro */}
              <div
                className='glass relative rounded-2xl overflow-hidden'
                style={{ boxShadow: "0 0 40px rgba(34,211,238,0.05)" }}
              >
                <div
                  className='absolute top-0 left-0 right-0 h-px pointer-events-none'
                  style={{
                    background:
                      "linear-gradient(90deg, transparent 5%, var(--c-cyan) 50%, transparent 95%)",
                  }}
                />
                <div className='p-8'>
                  <div className='flex items-start justify-between mb-6'>
                    <div className='flex items-center gap-4'>
                      <div
                        className='w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0'
                        style={{
                          background: "linear-gradient(135deg, #0891b2, #6366f1)",
                          boxShadow: "0 4px 20px rgba(8,145,178,0.30)",
                        }}
                      >
                        <Activity className='w-7 h-7 text-white' />
                      </div>
                      <div>
                        <h3 className='text-xl font-bold mb-0.5' style={{ color: "var(--c-text)" }}>
                          StockSignals Pro
                        </h3>
                        <p className='text-sm' style={{ color: "var(--c-text-2)" }}>
                          Real-time market intelligence
                        </p>
                      </div>
                    </div>
                    <span
                      className='badge-live flex-shrink-0 px-3 py-1 rounded-full text-xs font-semibold'
                      style={{ fontFamily: "var(--font-mono)" }}
                    >
                      ● Live
                    </span>
                  </div>

                  <p className='text-sm leading-relaxed mb-6' style={{ color: "var(--c-text-2)" }}>
                    Institutional-grade market intelligence powered by AI models and real-time data
                    pipelines. Continuously scans markets and surfaces the signals that matter.
                  </p>

                  <div className='grid grid-cols-2 gap-2 mb-7'>
                    {["Real-Time Signals", "AI Analytics", "20+ Indicators", "Custom Alerts"].map(
                      (feat) => (
                        <div
                          key={feat}
                          className='flex items-center gap-2 text-xs'
                          style={{ color: "var(--c-text-2)" }}
                        >
                          <span
                            className='w-1 h-1 rounded-full flex-shrink-0'
                            style={{ background: "var(--c-cyan)" }}
                          />
                          {feat}
                        </div>
                      ),
                    )}
                  </div>

                  <div className='flex gap-3'>
                    <a
                      href={PRODUCT_URL}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='btn-cta inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm'
                    >
                      Launch Platform <ArrowRight className='w-4 h-4' />
                    </a>
                    <Link
                      to='/register'
                      className='btn-ghost inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm'
                    >
                      Sign Up Free
                    </Link>
                  </div>
                </div>
              </div>

              {/* ── Product 2 — DataAgents (placeholder) ── */}
              <div
                className='relative rounded-2xl overflow-hidden'
                style={{
                  opacity: 0.78,
                  background:
                    "linear-gradient(145deg, rgba(34,38,58,0.9) 0%, rgba(28,31,46,0.9) 100%)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  borderTopColor: "rgba(245,158,11,0.22)",
                  boxShadow: "4px 4px 16px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.06) inset",
                }}
              >
                {/* Amber accent top */}
                <div
                  style={{
                    height: 1,
                    background:
                      "linear-gradient(90deg, transparent 5%, var(--c-amber) 50%, transparent 95%)",
                  }}
                />

                {/* Diagonal "In Dev" watermark lines */}
                <div
                  className='absolute inset-0 pointer-events-none'
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(135deg, rgba(245,158,11,0.025) 0px, rgba(245,158,11,0.025) 1px, transparent 1px, transparent 20px)",
                  }}
                />

                <div className='absolute top-5 right-5'>
                  <span
                    className='badge-dev px-3 py-1 rounded-full text-xs font-semibold'
                    style={{ fontFamily: "var(--font-mono)" }}
                  >
                    In Dev
                  </span>
                </div>

                <div className='p-8'>
                  <div className='flex items-center gap-4 mb-6'>
                    <div
                      className='w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0'
                      style={{
                        background:
                          "linear-gradient(145deg, rgba(50,38,10,0.8) 0%, rgba(40,28,5,0.8) 100%)",
                        border: "1px solid rgba(245,158,11,0.22)",
                        borderTopColor: "rgba(245,158,11,0.35)",
                        boxShadow: "3px 3px 10px rgba(0,0,0,0.45)",
                      }}
                    >
                      <Cpu className='w-7 h-7' style={{ color: "var(--c-amber)" }} />
                    </div>
                    <div>
                      <h3 className='text-xl font-bold mb-0.5' style={{ color: "var(--c-text)" }}>
                        DataAgents
                      </h3>
                      <p className='text-sm' style={{ color: "var(--c-text-2)" }}>
                        Autonomous data pipeline agents
                      </p>
                    </div>
                  </div>

                  <p className='text-sm leading-relaxed mb-6' style={{ color: "var(--c-text-2)" }}>
                    Fully autonomous AI agents that ingest, analyse, and act on enterprise data
                    pipelines. Currently in active development.
                  </p>

                  <div className='grid grid-cols-2 gap-2 mb-7'>
                    {[
                      "Autonomous Agents",
                      "No-Code Setup",
                      "Pipeline Orchestration",
                      "Enterprise Ready",
                    ].map((feat) => (
                      <div
                        key={feat}
                        className='flex items-center gap-2 text-xs'
                        style={{ color: "var(--c-text-3)" }}
                      >
                        <span
                          className='w-1 h-1 rounded-full flex-shrink-0'
                          style={{ background: "var(--c-amber)", opacity: 0.6 }}
                        />
                        {feat}
                      </div>
                    ))}
                  </div>

                  {/* 2D placeholder progress indicator */}
                  <div className='mb-6'>
                    <div className='flex items-center justify-between mb-2'>
                      <span
                        className='text-xs'
                        style={{ fontFamily: "var(--font-mono)", color: "var(--c-text-3)" }}
                      >
                        Development progress
                      </span>
                      <span
                        className='text-xs font-semibold'
                        style={{ fontFamily: "var(--font-mono)", color: "var(--c-amber)" }}
                      >
                        22%
                      </span>
                    </div>
                    <div
                      className='h-1.5 rounded-full overflow-hidden'
                      style={{
                        background: "rgba(245,158,11,0.10)",
                        border: "1px solid rgba(245,158,11,0.15)",
                      }}
                    >
                      <div
                        className='h-full rounded-full'
                        style={{
                          width: "22%",
                          background: "linear-gradient(90deg, #f59e0b, #fb923c)",
                          boxShadow: "0 0 10px rgba(245,158,11,0.4)",
                        }}
                      />
                    </div>
                  </div>

                  <Link
                    to='/contact'
                    className='btn-ghost inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm'
                    style={{ color: "var(--c-text-2)" }}
                  >
                    Express Interest <ArrowRight className='w-4 h-4' />
                  </Link>
                </div>
              </div>
            </div>

            <p className='text-center mt-6 text-sm' style={{ color: "var(--c-text-3)" }}>
              More products coming soon.{" "}
              <Link to='/contact' className='hover:underline' style={{ color: "var(--c-cyan)" }}>
                Follow our roadmap →
              </Link>
            </p>
          </div>
        </section>

        {/* ── FINAL CTA ─────────────────────────────────────────────────── */}
        <section
          className='relative py-28 px-6 overflow-hidden'
          style={{ borderTop: `1px solid ${borderColor}` }}
        >
          {/* Glow orb */}
          <div
            className='absolute inset-0 pointer-events-none'
            style={{
              background:
                "radial-gradient(ellipse at 50% 50%, rgba(8,145,178,0.12) 0%, rgba(99,102,241,0.06) 50%, transparent 70%)",
            }}
          />

          <div className='relative max-w-2xl mx-auto text-center'>
            <div className='flex justify-center mb-8'>
              <SpinningLogo size='lg' showText={false} />
            </div>
            <h2
              className='font-display leading-none mb-5'
              style={{ fontSize: "clamp(2.5rem, 7vw, 5.5rem)", color: "var(--c-text)" }}
            >
              READY TO UNLOCK
              <br />
              <span
                style={{
                  background: "linear-gradient(120deg, var(--c-cyan) 0%, var(--c-indigo) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                YOUR DATA?
              </span>
            </h2>
            <p className='text-lg mb-10 max-w-md mx-auto' style={{ color: "var(--c-text-2)" }}>
              Join a growing community of analysts and traders who trust Larxnet.
            </p>
            <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
              <Link
                to='/register'
                className='btn-cta group inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold'
              >
                Get Started Free
                <ArrowRight className='w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5' />
              </Link>
              <Link
                to='/contact'
                className='btn-ghost inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold'
              >
                Talk to Sales
              </Link>
            </div>
            <div
              className='flex flex-wrap justify-center gap-6 mt-8 text-sm'
              style={{ color: "var(--c-text-3)" }}
            >
              {["No credit card required", "Enterprise-ready"].map((txt) => (
                <span key={txt} className='flex items-center gap-1.5'>
                  <Check className='w-3.5 h-3.5' style={{ color: "var(--c-cyan)" }} />
                  {txt}
                </span>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default HomePage
